// ** Utils
import { http } from 'src/@core/utils/http';

// ** Config
import apiConfig from 'src/configs/api';

// ** Redux
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// ** Utils
import localStorageService from 'src/@core/utils/local-storage';

// ** Get Tickets
export const getTickets = createAsyncThunk('tickets/getTickets', async (params, { rejectWithValue }) => {
  const { ...other } = params;

  for (const key of Object.keys(other)) {
    if (other[key] === '') {
      delete other[key];
    }
  }

  try {
    const response = await http.get(apiConfig.getTicketsEndpoint, {
      params: {
        ...other,
      },
    });
    const data = response.data;

    return {
      tickets: data.results || [],
      count: data.count || 0,
      filters: params || {},
    };
  } catch (error) {
    return rejectWithValue({
      error: error.response.data,
      filters: params || {},
    });
  }
});

// ** Create Ticket
export const createTicket = createAsyncThunk('tickets/createTicket', async (params, { rejectWithValue }) => {
  const { form_data, channel, attachments, transport } = params;

  try {
    const fd = new FormData();
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    fd.append('channel', channel);
    fd.append('form_data', JSON.stringify(form_data));
    fd.append('transport', transport);

    for (let i = 0; i < attachments.length; i++) {
      fd.append('attachments', attachments[i]);
    }

    const response = await http.post(apiConfig.createTicketEndpoint, fd, config);
    const data = response.data;

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Edit Ticket
export const editTicket = createAsyncThunk('tickets/editTicket', async (params, { rejectWithValue }) => {
  try {
    const response = await http.patch(apiConfig.editTicketEndpoint.replace(':id', params.id), params);
    const data = response.data;

    return {
      ticket: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Delete Ticket
export const deleteTicket = createAsyncThunk('tickets/deleteTicket', async (params, { rejectWithValue, dispatch }) => {
  try {
    const response = await http.delete(apiConfig.deleteTicketEndpoint.replace(':id', params.id));
    const data = response.data;

    dispatch(
      getTickets({
        channel__contract: params.contract,
      }),
    );

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Spam Ticket
export const spamTicket = createAsyncThunk('tickets/spamTicket', async (params, { rejectWithValue, dispatch, getState }) => {
  const store = getState();
  const { ticketsFilters } = store.tickets;

  try {
    const response = await http.patch(apiConfig.editTicketEndpoint.replace(':id', params.id), {
      spam: params.spam,
    });
    const data = response.data;

    dispatch(
      getTickets({
        ...ticketsFilters,
        channel__contract: params.contract,
        spam: params.fetchSpam || !params.spam,
      }),
    );

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Ban Email Ticket
export const banEmailTicket = createAsyncThunk('tickets/banEmailTicket', async (params, { rejectWithValue, dispatch }) => {
  try {
    const response = await http.post(apiConfig.banEmailTicketEndpoint, {
      email: params.email,
      channel: params.channel,
    });
    const data = response.data;

    dispatch(
      spamTicket({
        contract: params.contract,
        spam: true,
        fetchSpam: !params.spam,
        id: params.id,
      }),
    );

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Purge Spam
export const purgeSpam = createAsyncThunk('tickets/purgeSpam', async (params, { rejectWithValue, dispatch, getState }) => {
  const store = getState();
  const { ticketsFilters } = store.tickets;

  try {
    const response = await http.post(apiConfig.purgeSpamContractEndpoint.replace(':id', params.contract));
    const data = response.data;

    dispatch(
      getTickets({
        ...ticketsFilters,
        channel__contract: params.contract,
      }),
    );

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Get Ticket
export const getTicket = createAsyncThunk('tickets/getTicket', async (params, { rejectWithValue }) => {
  try {
    const response = await http.get(apiConfig.getTicketEndpoint.replace(':id', params.id));
    const data = response.data;

    return {
      ticket: data || {},
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Get ticket executors
export const getTicketExecutors = createAsyncThunk('tickets/getTicketExecutors', async (params, { rejectWithValue }) => {
  try {
    const response = await http.get(apiConfig.getUsersEndpoint.replace(':id', params.contract), {
      params: {
        page_size: 999,
      },
    });
    const data = response.data.results;

    return {
      ticketExecutors: data || [],
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Get Ticket Tasks
export const getTicketTasks = createAsyncThunk('tickets/getTicketTasks', async (params, { rejectWithValue }) => {
  try {
    const response = await http.get(apiConfig.getTicketTasksEndpoint.replace(':id', params.id), {
      params: {
        page: params.page || 1,
        page_size: params.page_size || 999,
      },
    });
    const data = response.data;

    return {
      ticketTasks: data.results || [],
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Edit Ticket Task
export const editTicketTask = createAsyncThunk('tickets/editTicketTask', async (params, { rejectWithValue, dispatch }) => {
  const { id, task, ...other } = params;

  try {
    const response = await http.patch(apiConfig.editTicketTaskEndpoint.replace(':id', id).replace(':task', task), other);
    const data = response.data;

    dispatch(getTicketTasks({ id: id }));

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Delete Ticket Task
export const deleteTicketTask = createAsyncThunk('tickets/deleteTicketTask', async (params, { rejectWithValue, dispatch }) => {
  const { id, task } = params;

  try {
    const response = await http.delete(apiConfig.deleteTicketTaskEndpoint.replace(':id', id).replace(':task', task));
    const data = response.data;

    dispatch(getTicketTasks({ id: id }));

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Create Ticket Task
export const createTicketTask = createAsyncThunk('tickets/createTicketTask', async (params, { rejectWithValue, dispatch }) => {
  const { id, ...other } = params;

  try {
    const response = await http.post(apiConfig.createTicketTaskEndpoint.replace(':id', id), other);
    const data = response.data;

    dispatch(getTicketTasks({ id: id }));

    return {
      data: data,
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Get Ticket Activities
export const getTicketActivities = createAsyncThunk('tickets/getTicketActivities', async (params, { rejectWithValue }) => {
  try {
    const response = await http.get(apiConfig.getTicketActivitiesEndpoint, {
      params: {
        page_size: 999,
        ticket: params.id,
        ordering: 'created',
        order: '-created',
      },
    });
    const data = response.data;

    return {
      ticketActivities: data.results || [],
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

// ** Post Ticket Comment
export const postTicketComment = createAsyncThunk(
  'tickets/postTicketComment',
  async (params, { rejectWithValue, dispatch, getState }) => {
    const ticketId = getState().tickets.ticket.id;

    try {
      const response = await http.post(
        apiConfig.addTicketCommentEndpoint,
        {
          action: params.action,
          action_type: params.action_type || 'comment',
          ticket: params.id,
          visibility: params.visibility,
          ...(params.reply_to
            ? {
                reply_to: params.reply_to,
              }
            : {}),
          ...(params.attachments
            ? {
                attachments: params.attachments,
              }
            : {}),
        },
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );

      const data = response.data;

      if (!params.noReload) {
        dispatch(getTicketActivities({ id: ticketId }));
      }

      return {
        comment: data || {},
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** Delete Ticket Comment
export const deleteTicketComment = createAsyncThunk(
  'tickets/deleteTicketComment',
  async (params, { rejectWithValue, dispatch, getState }) => {
    const ticketId = getState().tickets.ticket.id;

    try {
      const response = await http.delete(apiConfig.deleteTicketCommentEndpoint.replace(':id', params.id));

      const data = response.data;

      dispatch(getTicketActivities({ id: ticketId }));

      return {
        data: data || {},
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// ** Get Ticket Contract
export const getTicketContract = createAsyncThunk('tickets/getTicketContract', async (params, { rejectWithValue }) => {
  try {
    const response = await http.get(apiConfig.getContractEndpoint.replace(':id', params.contract));
    const data = response.data;

    return {
      ticketContract: data || {},
    };
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
});

export const ticketsSlice = createSlice({
  name: 'tickets',
  initialState: {
    // ** Loading states:
    isLoadingTickets: false,
    isLoadingTicket: false,
    isLoadingTicketActivities: false,
    isLoadingTicketExecutors: false,
    isLoadingTicketTasks: false,
    isLoadingTicketContract: false,
    // ** Data states:
    tickets: [],
    ticket: {},
    ticketActivities: [],
    ticketExecutors: [],
    ticketTasks: [],
    ticketContract: {},
    // ** Count states:
    ticketsCount: 0,
    // ** Error states:
    ticketsError: false,
    ticketError: false,
    ticketActivitiesError: false,
    ticketExecutorsError: false,
    ticketTasksError: false,
    ticketContractError: false,
    // ** Filter states:
    ticketsFilters: {
      page: 1,
      page_size: localStorageService.get('ticketsFilters')?.page_size || 25,
      status: localStorageService.get('ticketsFilters')?.status || '',
      ordering: localStorageService.get('ticketsFilters')?.ordering || 'id',
    },
  },
  reducers: {
    resetTicket: (state) => {
      state.isLoadingTicket = false;
      state.ticketError = false;
      state.ticket = {};
      state.ticketActivities = [];
      state.ticketTasks = [];
      state.ticketContract = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTickets.pending, (state) => {
        state.isLoadingTickets = true;
        state.ticketsError = false;
      })
      .addCase(getTickets.fulfilled, (state, action) => {
        state.tickets = action.payload.tickets;
        state.ticketsCount = action.payload.count;
        state.ticketsFilters = action.payload.filters;

        state.isLoadingTickets = false;

        localStorageService.set('ticketsFilters', action.payload.filters);
      })
      .addCase(getTickets.rejected, (state, action) => {
        state.tickets = [];
        state.ticketsCount = 0;
        state.ticketsFilters = action.payload.filters;
        state.ticketsError = action.error?.payload?.error?.detail || 'Произошла ошибка';

        state.isLoadingTickets = false;

        localStorageService.set('ticketsFilters', action.payload.filters);
      });
    builder
      .addCase(getTicket.pending, (state) => {
        state.isLoadingTicket = true;

        state.ticketError = false;
      })
      .addCase(getTicket.fulfilled, (state, action) => {
        state.ticket = action.payload.ticket;

        state.isLoadingTicket = false;
      })
      .addCase(getTicket.rejected, (state, action) => {
        state.isLoadingTicket = false;

        state.ticketError = action.payload?.detail || 'Произошла ошибка';
      });
    builder
      .addCase(getTicketActivities.pending, (state) => {
        state.isLoadingTicketActivities = true;

        state.ticketActivitiesError = false;
      })
      .addCase(getTicketActivities.fulfilled, (state, action) => {
        state.ticketActivities = action.payload.ticketActivities;

        state.isLoadingTicketActivities = false;
      })
      .addCase(getTicketActivities.rejected, (state, action) => {
        state.isLoadingTicketActivities = false;

        state.ticketActivitiesError = action.payload?.detail || 'Произошла ошибка';
      });
    builder
      .addCase(getTicketExecutors.pending, (state) => {
        state.isLoadingTicketExecutors = true;

        state.ticketExecutorsError = false;
      })
      .addCase(getTicketExecutors.fulfilled, (state, action) => {
        state.ticketExecutors = action.payload.ticketExecutors;

        state.isLoadingTicketExecutors = false;
      })
      .addCase(getTicketExecutors.rejected, (state, action) => {
        state.isLoadingTicketExecutors = false;

        state.ticketExecutorsError = action.payload?.detail || 'Произошла ошибка';
      });
    builder
      .addCase(getTicketTasks.pending, (state) => {
        state.isLoadingTicketTasks = true;

        state.ticketTasksError = false;
      })
      .addCase(getTicketTasks.fulfilled, (state, action) => {
        state.ticketTasks = action.payload.ticketTasks;

        state.isLoadingTicketTasks = false;
      })
      .addCase(getTicketTasks.rejected, (state, action) => {
        state.isLoadingTicketTasks = false;

        state.ticketTasksError = action.payload?.detail || 'Произошла ошибка';
      });
    builder
      .addCase(getTicketContract.pending, (state) => {
        state.isLoadingTicketContract = true;

        state.ticketContractError = false;
      })
      .addCase(getTicketContract.fulfilled, (state, action) => {
        state.ticketContract = action.payload.ticketContract;

        state.isLoadingTicketContract = false;
      })
      .addCase(getTicketContract.rejected, (state, action) => {
        state.isLoadingTicketContract = false;

        state.ticketContractError = action.payload?.detail || 'Произошла ошибка';
      });
  },
});

export const { resetTicket } = ticketsSlice.actions;

export default ticketsSlice.reducer;
